import React from 'react';
import { Card, CardContent } from './ui/card';
import { 
  Zap, Shield, Globe, Cpu, Mic, Cloud, 
  Languages, Headphones, Sparkles, Clock,
  BarChart, Users
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface Feature {
  icon: React.ElementType;
  title: string;
  description: string;
  gradient: string;
  delay: number;
}

const features: Feature[] = [
  {
    icon: Zap,
    title: 'Lightning Fast',
    description: 'Real-time transcription with zero lag. Speak naturally and see your words appear instantly.',
    gradient: 'from-yellow-500 to-orange-500',
    delay: 0,
  },
  {
    icon: Shield,
    title: 'Privacy First',
    description: 'All processing happens locally on your device. Your voice data never leaves your computer.',
    gradient: 'from-green-500 to-emerald-500',
    delay: 100,
  },
  {
    icon: Globe,
    title: '50+ Languages',
    description: 'Support for major world languages with accent detection and dialect understanding.',
    gradient: 'from-blue-500 to-cyan-500',
    delay: 200,
  },
  {
    icon: Cpu,
    title: 'AI-Powered',
    description: 'Advanced neural networks trained on millions of hours of speech for unmatched accuracy.',
    gradient: 'from-purple-500 to-pink-500',
    delay: 300,
  },
  {
    icon: Mic,
    title: 'Voice Commands',
    description: 'Control formatting, punctuation, and editing with simple voice commands.',
    gradient: 'from-red-500 to-rose-500',
    delay: 400,
  },
  {
    icon: Cloud,
    title: 'Works Offline',
    description: 'No internet required. Perfect for secure environments or when traveling.',
    gradient: 'from-indigo-500 to-purple-500',
    delay: 500,
  },
];

const stats = [
  { label: 'Words Per Minute', value: '160+', icon: BarChart },
  { label: 'Accuracy Rate', value: '99.9%', icon: Shield },
  { label: 'Active Users', value: '50K+', icon: Users },
  { label: 'Time Saved Daily', value: '2.5hrs', icon: Clock },
];

export function Features() {
  return (
    <section id="features" className="py-24 relative">
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-primary">Powerful Features</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold">
              Everything You Need to
              <span className="text-primary"> 3x Your Productivity</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Built for professionals who value their time. Every feature is designed to help you 
              create content faster and more efficiently.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {features.map((feature, index) => (
              <Card
                key={index}
                className={cn(
                  "group relative overflow-hidden border-muted/50 hover:border-primary/50 transition-all duration-300",
                  "hover:shadow-lg hover:shadow-primary/10 hover:-translate-y-1",
                  "animate-slide-up opacity-0"
                )}
                style={{ animationDelay: `${feature.delay}ms`, animationFillMode: 'forwards' }}
              >
                <CardContent className="pt-6">
                  {/* Gradient Background */}
                  <div className={cn(
                    "absolute -top-20 -right-20 w-40 h-40 rounded-full opacity-10 blur-3xl transition-all duration-500",
                    `bg-gradient-to-br ${feature.gradient}`,
                    "group-hover:opacity-20 group-hover:scale-150"
                  )} />
                  
                  {/* Icon */}
                  <div className={cn(
                    "h-12 w-12 rounded-lg flex items-center justify-center mb-4",
                    `bg-gradient-to-br ${feature.gradient}`,
                    "shadow-lg transform transition-transform group-hover:scale-110 group-hover:rotate-3"
                  )}>
                    <feature.icon className="h-6 w-6 text-white" />
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Stats Section */}
          <div className="relative rounded-2xl bg-gradient-to-br from-primary/10 via-primary/5 to-secondary/10 p-1">
            <div className="rounded-2xl bg-background/80 backdrop-blur-sm p-8">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                {stats.map((stat, index) => (
                  <div
                    key={index}
                    className={cn(
                      "text-center space-y-2 animate-scale-in opacity-0"
                    )}
                    style={{ animationDelay: `${600 + index * 100}ms`, animationFillMode: 'forwards' }}
                  >
                    <stat.icon className="h-8 w-8 mx-auto text-primary mb-2" />
                    <div className="text-3xl font-bold bg-gradient-to-br from-primary to-primary/60 bg-clip-text text-transparent">
                      {stat.value}
                    </div>
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="mt-16 text-center">
            <p className="text-lg text-muted-foreground mb-6">
              And that's just the beginning. Discover all features in action.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <button className="group relative px-6 py-3 font-medium">
                <span className="relative z-10 flex items-center gap-2">
                  See All Features
                  <span className="transition-transform group-hover:translate-x-1">→</span>
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-primary/0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}